import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useLanguage } from "../context/LanguageContext";

export const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const GREEK_MONTH_LABELS = ["Ιαν", "Φεβ", "Μαρ", "Απρ", "Μάι", "Ιουν", "Ιουλ", "Αυγ", "Σεπ", "Οκτ", "Νοε", "Δεκ"];

/**
 * A monthly override for a value that already has a source.
 *
 * COLLAPSED BY DEFAULT, AND THAT IS THE POINT.
 *
 * The first version showed twelve boxes permanently, pre-filled with the seed
 * value. Two things went wrong with that and both were mine:
 *
 *   1. Twelve boxes reading 0.20 look SET. The caption said "not set" while the
 *      fields said otherwise, and the fields win -- so a user could not tell
 *      "using the surface type" from "I typed 0.20 twelve times".
 *   2. It made the setting it depends on look pointless. If the albedo is
 *      sitting right there in a grid, what is the surface type for? The answer
 *      is that the surface type PRODUCES that number, and a design that hides
 *      the arrow between them invites exactly that question.
 *
 * So the resting state is one line that names the value and where it came from,
 * with the grid one click away. Open it and you are overriding; closed, you are
 * not. The state of the control now matches the state of the data.
 */
export default function MonthlyValueGrid({
  label,                   // "Albedo", "Soiling"
  values,                  // array of 12, or null/undefined for "not set"
  onChange,                // (nextArrayOrNull) => void
  seedValue,               // the figure the source supplies
  seedLabel,               // what that source is, in words
  step = 0.01,
  min = 0,
  max = 1,
  unit = "",
  decimals = 2,
  helper = null,
  reference = null,        // [[label, value], ...] behind an info tooltip
  onOpenChange = null,     // told when the panel opens, so a caller can give it room
}) {
  const { language } = useLanguage();
  const isGreek = language === "el";
  const sourceLabel = isGreek
    ? ({ "surface type": "τον τύπο επιφάνειας", "annual figure": "την ετήσια τιμή" })[seedLabel] || seedLabel
    : seedLabel;
  const resetTarget = isGreek
    ? ({ "surface type": "στον τύπο επιφάνειας", "annual figure": "στην ετήσια τιμή" })[seedLabel] || `σε ${seedLabel || "την ετήσια τιμή"}`
    : seedLabel;
  const active = Array.isArray(values) && values.length === 12;
  const [open, setOpen] = useState(active);
  const previousActive = useRef(active);
  // Twelve month inputs need a row of their own. The caller cannot see this
  // state, and guessing at it from `values` is wrong: the panel opens before
  // anything is typed into it.
  const setOpenAndTell = setOpen;
  useEffect(() => {
    if (active && !previousActive.current) setOpen(true);
    previousActive.current = active;
  }, [active]);
  useEffect(() => {
    onOpenChange?.(open);
  }, [onOpenChange, open]);
  const [common, setCommon] = useState("");

  const shown = useMemo(
    () => (active ? values : new Array(12).fill(seedValue)),
    [active, values, seedValue]
  );

  const setMonth = (index, raw) => {
    const next = [...shown];
    const parsed = raw === "" ? seedValue : Number(raw);
    next[index] = Number.isFinite(parsed) ? parsed : seedValue;
    onChange(next);
  };

  const applyCommon = () => {
    const parsed = Number(common);
    if (!Number.isFinite(parsed)) return;
    onChange(new Array(12).fill(parsed));
  };

  const reset = () => {
    onChange(null);
    setCommon("");
    setOpenAndTell(false);
  };

  const summary = active
    ? `${Math.min(...values).toFixed(decimals)}–${Math.max(...values).toFixed(decimals)}${unit}${isGreek ? " ανά μήνα" : " by month"}`
    : `${Number(seedValue).toFixed(decimals)}${unit}${seedLabel ? isGreek ? ` · από ${sourceLabel}` : ` · from the ${seedLabel}` : ""}`;

  return (
    <Box>
      {/* The resting state: what the value is, where it comes from, and one way in. */}
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap" }} useFlexGap>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {summary}
        </Typography>
        {reference && reference.length > 0 && (
          <Tooltip
            arrow
            title={
              <Box sx={{ py: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
                  Usual values
                </Typography>
                {reference.map(([name, value]) => (
                  <Box key={name} sx={{ display: "flex", gap: 2, justifyContent: "space-between" }}>
                    <Typography variant="caption">{name}</Typography>
                    <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            }
          >
            <IconButton size="small" tabIndex={-1} sx={{ p: 0.25 }}>
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
        <Button
          size="small"
          onClick={() => setOpenAndTell((v) => !v)}
          endIcon={
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 120ms",
              }}
            />
          }
          sx={{ minWidth: 0, py: 0, px: 0.75, textTransform: "none" }}
        >
          {active ? "Edit months" : "Vary by month"}
        </Button>
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "action.hover",
          }}
        >
          {/* Give each month enough room even when this panel is shown inside
              a narrow settings column. Cap the grid at six fields per row. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 104px), 1fr))",
              gap: 1,
              maxWidth: 760,
            }}
          >
            {MONTH_LABELS.map((month, index) => (
              <TextField
                key={month}
                size="small"
                label={isGreek ? GREEK_MONTH_LABELS[index] : month}
                type="number"
                value={
                  Number.isFinite(shown[index])
                    ? Number(shown[index].toFixed(decimals))
                    : ""
                }
                onChange={(e) => setMonth(index, e.target.value)}
                inputProps={{ step, min, max, style: { padding: "6px 8px" } }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 0,
                  "& .MuiInputBase-input": { fontVariantNumeric: "tabular-nums" },
                  "& .MuiInputBase-root": { bgcolor: "background.paper" },
                }}
              />
            ))}
          </Box>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 1.25, flexWrap: "wrap" }}
            useFlexGap
          >
            <TextField
              size="small"
              label={`${isGreek ? "Ορισμός όλων" : "Set all"}${unit ? ` (${unit})` : ""}`}
              type="number"
              value={common}
              onChange={(e) => setCommon(e.target.value)}
              inputProps={{ step, min, max, style: { padding: "6px 8px" } }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, maxWidth: "100%", "& .MuiInputBase-root": { bgcolor: "background.paper" } }}
            />
            <Button size="small" variant="outlined" onClick={applyCommon}>
              Apply
            </Button>
            <Box sx={{ flex: 1 }} />
            {active && (
              <Button size="small" color="warning" onClick={reset}>
                {isGreek ? `Επαναφορά ${resetTarget}` : `Reset to ${seedLabel || "the annual figure"}`}
              </Button>
            )}
          </Stack>

          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mt: 0.75 }}
          >
            {active ? (
              isGreek ? "Το μοντέλο χρησιμοποιεί αυτές τις δώδεκα μηνιαίες τιμές." : "These twelve values are what the model runs on."
            ) : isGreek ? (
              <>Δεν έχει οριστεί διαφορετική τιμή — κάθε μήνας χρησιμοποιεί <b>{Number(seedValue).toFixed(decimals)}{unit}</b>{seedLabel ? ` από ${sourceLabel}` : ""}. Πληκτρολογήστε σε ένα πεδίο για να αρχίσει η μηνιαία προσαρμογή.</>
            ) : (
              <>Nothing is overridden yet — every month uses <b>{Number(seedValue).toFixed(decimals)}{unit}</b>{seedLabel ? ` from the ${seedLabel}` : ""}. Typing in a box starts the override.</>
            )}
            {helper ? <> {helper}</> : null}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}
